import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { 
  initializeBuffers, 
  updateBufferSizes,
  changePinnedSide,
  sendBufferToWall,
  deleteBuffer,
  resizeBuffer,
  migrateOldBufferFormat,
  calculateUsableWidth,
  getBuffersOnWall,
  getAllValidWalls,
} from '@/features/design/utils/cornerBuffer';
import { CATALOG_CATEGORIES } from '@/features/design/utils/catalog';
import {
  deriveColumnsFromItems,
  getColumnForItem,
  calculateHorizontalReflow,
  calculateDepthSync,
  validateColumnWidthChange,
  recalculateColumnPositions,
  calculateVerticalReflow,
  canDuplicateColumn,
  createColumnDuplicates,
  canCopyWallLayout,
  createWallLayoutCopy,
  getColumnItemIds,
} from '@/features/design/utils/columns';
import { createAuthSlice } from './slices/authSlice';
import { createUiSlice } from './slices/uiSlice';
import { apiCall } from './utils/apiCall';
import { computeCornerHeight, sectionHeight as cornerSectionHeight, CORNER_ALLOWED_TYPES, CORNER_BLOCKED_TYPES } from '@/features/design/utils/corner';
import {
  SHAPE_TYPES,
  L_SHAPE_DOOR_SIDES,
  U_SHAPE_DOOR_CONFIGS,
  createDefaultProject,
  createWallsForShape,
} from './utils/projectFactory';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// SHAPE_TYPES, L_SHAPE_DOOR_SIDES, U_SHAPE_DOOR_CONFIGS, createDefaultProject,
// and createWallsForShape were extracted to `./utils/projectFactory.js`
// (Phase D split). They're still re-exported as named exports at the
// bottom of this file so existing call-sites
// `import useStore, { SHAPE_TYPES } from '@/store/useStore'` keep working.

const MAX_HISTORY = 50;

// API helper now lives in `./utils/apiCall.js` (Phase D extraction).
// Imported at the top of this file.

const useStore = create(
  persist(
    (set, get) => ({
      // === Auth slice (Phase D extraction) ===
      ...createAuthSlice(set, get, { apiCall }),

      // === UI slice (Phase D extraction) ===
      ...createUiSlice(set, get),

      // Project state
      project: null,
      activeWallId: 'wall-a',
      selectedItemId: null,
      isColumnSelected: false,
      
      // Saved projects list
      savedProjects: [],
      
      // (UI state moved to createUiSlice — isLoading, zoom, snap*, modal flags)
      currentPage: 'landing',
      
      // Editor Context - EXPLICIT navigation contexts
      // contextType: "project" | "booking_create_design" | "booking_edit_design"
      editorContext: null,
      /* Example editorContext:
      {
        contextType: "booking_edit_design",
        bookingId: "abc123",
        returnTo: { route: "admin", state: { activeTab: "bookings", fromCalendar: true } },
        source: "bookings_list" | "calendar" | "deep_link" | "my_projects"
      }
      */
      
      // Legacy navigation context - for tracking return paths (keeping for compatibility)
      navigationContext: null,
      
      // (Auth state moved to createAuthSlice)
      
      // (Modal flags moved to createUiSlice)
      
      // Booking mode (true = Book & Customize without design)
      bookWithoutDesign: false,
      
      // Edit mode state (for editing room settings without resetting project)
      editMode: false,
      editModeSnapshot: null, // Snapshot of project state before entering edit mode
      
      // Track if design has unsaved changes
      hasUnsavedChanges: false,
      
      // History for undo/redo
      history: [],
      historyIndex: -1,
      
      // Flag to prevent multiple initializations
      _initialized: false,
      
      // Actions
      initializeApp: () => {
        // Prevent multiple initializations (React StrictMode calls twice)
        if (get()._initialized) return;
        set({ _initialized: true });
        
        // Check for existing auth token
        const token = localStorage.getItem('eoc-auth-token');
        if (token) {
          apiCall('/api/auth/me')
            .then(user => {
              set({ currentUser: user, authToken: token });
              try { localStorage.setItem('eoc-current-user', JSON.stringify(user)); } catch (err) { console.warn('[auth] cache user failed', err); }
              try { window.dispatchEvent(new Event('authChanged')); } catch (err) { console.warn('[auth] dispatch failed', err); }
            })
            .catch(() => {
              localStorage.removeItem('eoc-auth-token');
              localStorage.removeItem('eoc-current-user');
              try { window.dispatchEvent(new Event('authChanged')); } catch (err) { console.warn('[auth] dispatch failed', err); }
            });
        }
        
        // Restore editor context from localStorage if exists
        const storedContext = localStorage.getItem('eoc-editor-context');
        if (storedContext) {
          try {
            const context = JSON.parse(storedContext);
            set({ editorContext: context });
          } catch (e) {
            localStorage.removeItem('eoc-editor-context');
          }
        }
        
        // Determine initial page based on persisted state
        const state = get();
        const persistedPage = state.currentPage;
        const hasProject = state.project !== null;
        
        let initialPage = 'landing';
        
        // Pages that require a project to be loaded
        const projectRequiredPages = ['editor', 'viewer-3d', 'review', 'mobile-generate'];
        
        // Pages that are always valid
        const alwaysValidPages = ['landing', 'closet-type', 'room-setup', 'saved-designs', 'booking', 'admin'];
        
        if (persistedPage) {
          if (alwaysValidPages.includes(persistedPage)) {
            initialPage = persistedPage;
          } else if (projectRequiredPages.includes(persistedPage) && hasProject) {
            initialPage = persistedPage;
          } else if (hasProject) {
            initialPage = 'editor';
          }
        } else if (hasProject) {
          initialPage = 'editor';
        }
        
        set({ currentPage: initialPage });
      },
      
      setCurrentPage: (page) => set({ currentPage: page }),
      
      // Editor Context actions - NEW explicit navigation system
      setEditorContext: (context) => {
        // Also persist to localStorage for refresh safety
        if (context) {
          localStorage.setItem('eoc-editor-context', JSON.stringify(context));
        } else {
          localStorage.removeItem('eoc-editor-context');
        }
        set({ editorContext: context });
      },
      
      clearEditorContext: () => {
        localStorage.removeItem('eoc-editor-context');
        localStorage.removeItem('admin-editing-booking-id');
        localStorage.removeItem('admin-creating-for-customer');
        set({ editorContext: null });
      },
      
      // Restore editor context from localStorage (for refresh safety)
      restoreEditorContext: () => {
        const stored = localStorage.getItem('eoc-editor-context');
        if (stored) {
          try {
            const context = JSON.parse(stored);
            set({ editorContext: context });
            return context;
          } catch (e) {
            localStorage.removeItem('eoc-editor-context');
          }
        }
        return null;
      },
      
      // Navigation context actions (legacy - keeping for compatibility)
      setNavigationContext: (context) => set({ navigationContext: context }),
      clearNavigationContext: () => set({ navigationContext: null }),
      
      // Unsaved changes actions
      setHasUnsavedChanges: (hasChanges) => set({ hasUnsavedChanges: hasChanges }),
      // (setShowDiscardChangesDialog moved to createUiSlice)
      
      // Book & Customize mode setter
      setBookWithoutDesign: (value) => set({ bookWithoutDesign: value }),
      
      // Project actions
      createProject: (shapeType, roomConfig, lShapeDoorSide = L_SHAPE_DOOR_SIDES.RIGHT, uShapeDoorConfig = U_SHAPE_DOOR_CONFIGS.SINGLE) => {
        const project = createDefaultProject();
        project.shapeType = shapeType;
        project.lShapeDoorSide = lShapeDoorSide;
        project.uShapeDoorConfig = uShapeDoorConfig;
        project.walls = createWallsForShape(shapeType, null, lShapeDoorSide);
        // For Double Door U-shape, Wall B is not designed (it's an opening)
        if (shapeType === SHAPE_TYPES.U_SHAPE && uShapeDoorConfig === U_SHAPE_DOOR_CONFIGS.DOUBLE) {
          const wallB = project.walls.find(w => w.id === 'wall-b');
          if (wallB) wallB.designed = false;
        }
        project.room = { ...project.room, ...roomConfig };
        // Initialize corner buffers based on shape type and door side (new format)
        const depthIn = roomConfig?.defaultDepth || project.room.defaultDepth;
        // Double Door U-shape: no corner buffers
        if (shapeType === SHAPE_TYPES.U_SHAPE && uShapeDoorConfig === U_SHAPE_DOOR_CONFIGS.DOUBLE) {
          project.buffersByWall = {};
        } else {
          project.buffersByWall = initializeBuffers(shapeType, depthIn, lShapeDoorSide);
        }
        project.selectedBufferId = null;
        project.updatedAt = new Date().toISOString();
        
        // Set default active wall based on L-shape configuration
        const defaultWallId = (shapeType === SHAPE_TYPES.L_SHAPE && lShapeDoorSide === L_SHAPE_DOOR_SIDES.LEFT) 
          ? 'wall-b' 
          : 'wall-a';
        
        set({ 
          project, 
          activeWallId: defaultWallId,
          selectedItemId: null,
          history: [JSON.stringify(project)],
          historyIndex: 0,
          currentPage: 'editor',
        });
      },
      
      updateProject: (updates) => {
        const state = get();
        if (!state.project) return;
        
        const newProject = {
          ...state.project,
          ...updates,
          updatedAt: new Date().toISOString(),
        };
        
        // Add to history
        const newHistory = state.history.slice(0, state.historyIndex + 1);
        newHistory.push(JSON.stringify(newProject));
        if (newHistory.length > MAX_HISTORY) {
          newHistory.shift();
        }
        
        set({ 
          project: newProject,
          history: newHistory,
          historyIndex: newHistory.length - 1,
        });
      },
      
      loadProject: (projectData) => {
        // Ensure styleSettings exists for older projects
        // Migrate: add panelThickness if missing (older projects)
        if (!projectData.room) projectData.room = {};
        if (projectData.room.panelThickness == null) {
          projectData.room.panelThickness = 0.625;
        }
        if (!projectData.styleSettings) {
          projectData.styleSettings = {
            woodColor: 'naturalOak',
            customWoodColor: null,
            handleStyle: 'none',
            faceStyle: 'flat',
          };
        } else if (!projectData.styleSettings.faceStyle) {
          projectData.styleSettings.faceStyle = 'flat';
        }
        
        // Migrate or initialize buffersByWall for older projects
        const depthIn = projectData.room?.defaultDepth || 24;
        // Only migrate/initialize if buffersByWall is truly undefined (not just empty)
        // If buffersByWall exists (even with all null values), respect it - user may have deleted buffers
        if (projectData.buffersByWall === undefined) {
          // Check if we have old cornerBuffers format to migrate
          if (projectData.cornerBuffers && Object.keys(projectData.cornerBuffers).length > 0) {
            // Migrate from old format
            projectData.buffersByWall = migrateOldBufferFormat(
              projectData.cornerBuffers,
              projectData.shapeType,
              depthIn
            );
          } else {
            // Brand new project without any buffer data - initialize defaults
            projectData.buffersByWall = migrateOldBufferFormat(
              null,
              projectData.shapeType,
              depthIn
            );
          }
        }
        // If buffersByWall exists (including null values from deleted buffers), keep it as-is
        projectData.selectedBufferId = null;
        
        set({
          project: projectData,
          activeWallId: projectData.walls[0]?.id || 'wall-a',
          selectedItemId: null,
          history: [JSON.stringify(projectData)],
          historyIndex: 0,
          currentPage: 'editor',
        });
      },
      
      newProject: () => {
        set({
          project: null,
          activeWallId: 'wall-a',
          selectedItemId: null,
          history: [],
          historyIndex: -1,
          currentPage: 'closet-type',
        });
      },
      
      // Wall actions
      setActiveWall: (wallId) => set({ activeWallId: wallId, selectedItemId: null }),
      
      updateWall: (wallId, updates) => {
        const state = get();
        if (!state.project) return;
        
        const walls = state.project.walls.map(wall => 
          wall.id === wallId ? { ...wall, ...updates } : wall
        );
        
        state.updateProject({ walls });
      },
      
      // Item actions
      addItem: (item) => {
        const state = get();
        if (!state.project) return;
        
        const wallId = item.wallId || state.activeWallId;
        
        // Simply add the item without auto-creating any fillers
        const newItem = {
          id: uuidv4(),
          wallId: wallId,
          count: item.props?.count || 1,
          ...item,
        };
        
        state.updateProject({
          items: [...state.project.items, newItem],
        });
        
        set({ selectedItemId: newItem.id });
      },
      
      // Corner Shelf actions
      addCornerShelf: (cornerComponent) => {
        const state = get();
        if (!state.project) return;
        
        const cornerComponents = state.project.cornerComponents || [];
        const cornerId = uuidv4();
        const newCornerComponent = {
          id: cornerId,
          ...cornerComponent,
        };
        
        // Also add items to both walls for 2D rendering
        const cornerWalls = cornerComponent.cornerKey === 'AB' 
          ? ['wall-a', 'wall-b'] 
          : ['wall-b', 'wall-c'];
        
        const wall1 = state.project.walls.find(w => w.id === cornerWalls[0]);
        const wall2 = state.project.walls.find(w => w.id === cornerWalls[1]);
        
        const newItems = [];
        
        // Add to first wall (right side)
        if (wall1) {
          newItems.push({
            id: uuidv4(),
            templateId: 'corner-shelf-template',
            type: 'Corner Shelf',
            category: 'corner_shelf',
            wallId: cornerWalls[0],
            width: cornerComponent.armIn,
            height: cornerComponent.heightIn,
            depth: cornerComponent.depthIn,
            x: wall1.length - cornerComponent.armIn,
            y: 0,
            shelfCount: cornerComponent.shelfCount,
            cornerSide: 'right',
            cornerKey: cornerComponent.cornerKey,
            isCornerComponent: true,
            cornerComponentId: cornerId,
            pairedWallId: cornerWalls[1],
          });
        }
        
        // Add to second wall (left side)
        if (wall2) {
          newItems.push({
            id: uuidv4(),
            templateId: 'corner-shelf-template',
            type: 'Corner Shelf',
            category: 'corner_shelf',
            wallId: cornerWalls[1],
            width: cornerComponent.armIn,
            height: cornerComponent.heightIn,
            depth: cornerComponent.depthIn,
            x: 0,
            y: 0,
            shelfCount: cornerComponent.shelfCount,
            cornerSide: 'left',
            cornerKey: cornerComponent.cornerKey,
            isCornerComponent: true,
            cornerComponentId: cornerId,
            pairedWallId: cornerWalls[0],
          });
        }
        
        state.updateProject({
          cornerComponents: [...cornerComponents, newCornerComponent],
          items: [...state.project.items, ...newItems],
        });
      },
      
      deleteCornerShelf: (cornerKey) => {
        const state = get();
        if (!state.project) return;
        
        const cornerComponents = (state.project.cornerComponents || []).filter(
          c => c.cornerKey !== cornerKey
        );
        
        // Also remove any related items
        const items = state.project.items.filter(
          i => i.cornerKey !== cornerKey
        );
        
        state.updateProject({ cornerComponents, items });
      },
      
      updateItem: (itemId, updates) => {
        const state = get();
        if (!state.project) return;
        
        const items = state.project.items.map(item =>
          item.id === itemId ? { ...item, ...updates } : item
        );
        
        state.updateProject({ items });
      },

      // ─── Corner Cabinet section actions ─────────────────────────
      // The Corner is a container: its `cornerItems` list holds the
      // vertical stack (Shelves, Hanging, etc.). Every mutation below
      // recomputes `height` from the stack (via computeCornerHeight)
      // so 2D + 3D stay in sync with no extra bookkeeping.

      // Which corner item is currently receiving new sections via the
      // Corner Add Section modal (null when the modal is closed).
      cornerAddSectionTargetId: null,
      openCornerAddSection: (itemId) => set({ cornerAddSectionTargetId: itemId }),
      closeCornerAddSection: () => set({ cornerAddSectionTargetId: null }),

      addCornerSection: (itemId, typeKey) => {
        const state = get();
        if (!state.project) return;
        if (CORNER_BLOCKED_TYPES.has(typeKey)) return;
        const meta = CORNER_ALLOWED_TYPES.find(t => t.key === typeKey);
        if (!meta) return;
        const items = state.project.items.map(it => {
          if (it.id !== itemId) return it;
          const section = { id: uuidv4(), type: typeKey, side: 'first', settings: { ...meta.defaults } };
          const nextSections = [...(it.cornerItems || []), section];
          return { ...it, cornerItems: nextSections, height: computeCornerHeight(nextSections) };
        });
        state.updateProject({ items });
      },

      updateCornerSection: (itemId, sectionId, patch) => {
        const state = get();
        if (!state.project) return;
        const items = state.project.items.map(it => {
          if (it.id !== itemId) return it;
          const nextSections = (it.cornerItems || []).map(sec =>
            sec.id === sectionId
              ? { ...sec, ...patch, settings: { ...sec.settings, ...(patch.settings || {}) } }
              : sec
          );
          return { ...it, cornerItems: nextSections, height: computeCornerHeight(nextSections) };
        });
        state.updateProject({ items });
      },

      removeCornerSection: (itemId, sectionId) => {
        const state = get();
        if (!state.project) return;
        const items = state.project.items.map(it => {
          if (it.id !== itemId) return it;
          const nextSections = (it.cornerItems || []).filter(sec => sec.id !== sectionId);
          return { ...it, cornerItems: nextSections, height: computeCornerHeight(nextSections) };
        });
        state.updateProject({ items });
      },

      reorderCornerSection: (itemId, sectionId, direction) => {
        const state = get();
        if (!state.project) return;
        const items = state.project.items.map(it => {
          if (it.id !== itemId) return it;
          const list = [...(it.cornerItems || [])];
          const idx = list.findIndex(s => s.id === sectionId);
          if (idx < 0) return it;
          const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
          if (swapIdx < 0 || swapIdx >= list.length) return it;
          [list[idx], list[swapIdx]] = [list[swapIdx], list[idx]];
          return { ...it, cornerItems: list, height: computeCornerHeight(list) };
        });
        state.updateProject({ items });
      },
      
      /**
       * Update an item and automatically reflow other items in the same column
       * to prevent overlaps when height changes
       */
      updateItemWithColumnReflow: (itemId, updates) => {
        const state = get();
        if (!state.project) return;
        
        const currentItem = state.project.items.find(item => item.id === itemId);
        if (!currentItem) return;
        
        // Only reflow if height is changing
        const isHeightChanging = updates.height !== undefined && updates.height !== currentItem.height;
        
        if (!isHeightChanging) {
          // No height change, just update normally
          const items = state.project.items.map(item =>
            item.id === itemId ? { ...item, ...updates } : item
          );
          state.updateProject({ items });
          return;
        }
        
        const newHeight = updates.height;
        const heightDelta = newHeight - currentItem.height;
        
        // Find all items in the same column (same wall, overlapping X range)
        const columnItems = state.project.items.filter(item => {
          if (item.wallId !== currentItem.wallId) return false;
          if (item.id === itemId) return true;
          
          // Check if items overlap in X (same column)
          const itemStart = item.x;
          const itemEnd = item.x + item.width;
          const currentStart = currentItem.x;
          const currentEnd = currentItem.x + currentItem.width;
          
          // Items overlap if their X ranges intersect
          return itemStart < currentEnd && itemEnd > currentStart;
        });
        
        // Sort by Y position (bottom to top: lowest Y first)
        columnItems.sort((a, b) => a.y - b.y);
        
        // Find the index of the current item
        const currentIndex = columnItems.findIndex(item => item.id === itemId);
        
        // Calculate adjustments needed
        const updatedItem = { ...currentItem, ...updates };
        const updatedItemTop = updatedItem.y + updatedItem.height;
        
        // Build the list of items with their new positions
        const itemUpdates = [];
        
        // Update the current item
        itemUpdates.push({ id: itemId, ...updates });
        
        // Check items ABOVE the current item (higher Y values)
        // These may need to move up if the current item grew
        for (let i = currentIndex + 1; i < columnItems.length; i++) {
          const aboveItem = columnItems[i];
          const aboveItemBottom = aboveItem.y;
          
          // If there's an overlap, push the item up
          if (aboveItemBottom < updatedItemTop) {
            const newY = updatedItemTop;
            itemUpdates.push({ id: aboveItem.id, y: newY });
          }
        }
        
        // Check items BELOW the current item (lower Y values)
        // These may need to move down if the current item moved down or grew downward
        // (In this app, Y=0 is floor, so "below" means lower Y)
        // For simplicity, we only push items UP, not down
        
        // Apply all updates
        const items = state.project.items.map(item => {
          const update = itemUpdates.find(u => u.id === item.id);
          return update ? { ...item, ...update } : item;
        });
        
        state.updateProject({ items });
      },
      
      // Update multiple items at once (for reorder operations)
      updateMultipleItems: (updatesArray) => {
        const state = get();
        if (!state.project) return;
        
        const items = state.project.items.map(item => {
          const update = updatesArray.find(u => u.id === item.id);
          return update ? { ...item, ...update } : item;
        });
        
        state.updateProject({ items });
      },
      
      /**
       * Update column width with horizontal reflow
       * When width changes on any item, all items in the same column get the new width,
       * and all columns to the right shift to maintain contiguous layout (no gaps)
       */
      updateColumnWidth: (itemId, newWidth) => {
        const state = get();
        if (!state.project || !state.activeWallId) return { success: false, error: 'No project or wall' };
        
        const currentItem = state.project.items.find(item => item.id === itemId);
        if (!currentItem) return { success: false, error: 'Item not found' };
        
        const wallId = currentItem.wallId || state.activeWallId;
        const wall = state.project.walls.find(w => w.id === wallId);
        if (!wall) return { success: false, error: 'Wall not found' };
        
        // Get usable width info (accounting for buffers)
        const usableInfo = calculateUsableWidth(
          state.project.buffersByWall,
          wallId,
          wall.length
        );
        
        // Validate the width change
        const validation = validateColumnWidthChange(
          state.project.items,
          wallId,
          itemId,
          newWidth,
          wall.length,
          usableInfo
        );
        
        if (!validation.valid) {
          return { success: false, error: validation.error };
        }
        
        // Calculate horizontal reflow
        const { updates, error } = calculateHorizontalReflow(
          state.project.items,
          wallId,
          itemId,
          newWidth,
          usableInfo.startOffset || 0
        );
        
        if (error) {
          return { success: false, error };
        }
        
        if (updates.length === 0) {
          return { success: true }; // No changes needed
        }
        
        // Apply all updates
        const items = state.project.items.map(item => {
          const update = updates.find(u => u.id === item.id);
          return update ? { ...item, ...update } : item;
        });
        
        state.updateProject({ items });
        return { success: true };
      },
      
      /**
       * Update column depth - syncs depth across all items in the same column
       */
      updateColumnDepth: (itemId, newDepth) => {
        const state = get();
        if (!state.project || !state.activeWallId) return { success: false, error: 'No project or wall' };
        
        const currentItem = state.project.items.find(item => item.id === itemId);
        if (!currentItem) return { success: false, error: 'Item not found' };
        
        const wallId = currentItem.wallId || state.activeWallId;
        
        // Calculate depth sync updates
        const { updates, error } = calculateDepthSync(
          state.project.items,
          wallId,
          itemId,
          newDepth
        );
        
        if (error) {
          return { success: false, error };
        }
        
        if (updates.length === 0) {
          return { success: true }; // No changes needed
        }
        
        // Apply all updates
        const items = state.project.items.map(item => {
          const update = updates.find(u => u.id === item.id);
          return update ? { ...item, ...update } : item;
        });
        
        state.updateProject({ items });
        return { success: true };
      },
      
      /**
       * Update item height with vertical reflow within column
       * Ensures no gaps between stacked items
       */
      updateColumnHeight: (itemId, newHeight) => {
        const state = get();
        if (!state.project || !state.activeWallId) return { success: false, error: 'No project or wall' };
        
        const currentItem = state.project.items.find(item => item.id === itemId);
        if (!currentItem) return { success: false, error: 'Item not found' };
        
        const wallId = currentItem.wallId || state.activeWallId;
        const wall = state.project.walls.find(w => w.id === wallId);
        const wallHeight = wall?.height || state.project.room?.height || 96;
        
        // Get the column
        const column = getColumnForItem(state.project.items, wallId, itemId);
        if (!column) return { success: false, error: 'Column not found' };
        
        // Calculate vertical reflow
        const updates = calculateVerticalReflow(column, itemId, newHeight);
        
        // Validate total height doesn't exceed wall height
        const totalHeight = column.items.reduce((sum, item) => {
          if (item.id === itemId) return sum + newHeight;
          return sum + item.height;
        }, 0);
        
        if (totalHeight > wallHeight) {
          return { 
            success: false, 
            error: `Total column height (${totalHeight.toFixed(1)}") exceeds wall height (${wallHeight}")`
          };
        }
        
        if (updates.length === 0) {
          // Just update the single item
          const items = state.project.items.map(item =>
            item.id === itemId ? { ...item, height: newHeight } : item
          );
          state.updateProject({ items });
          return { success: true };
        }
        
        // Apply all updates
        const items = state.project.items.map(item => {
          const update = updates.find(u => u.id === item.id);
          return update ? { ...item, ...update } : item;
        });
        
        state.updateProject({ items });
        return { success: true };
      },
      
      /**
       * Recalculate all column positions on a wall to close gaps
       * Call after deleting items or columns
       */
      reflowWallColumns: (wallId) => {
        const state = get();
        if (!state.project) return;
        
        const wall = state.project.walls.find(w => w.id === wallId);
        if (!wall) return;
        
        // Get buffer offset
        const usableInfo = calculateUsableWidth(
          state.project.buffersByWall,
          wallId,
          wall.length
        );
        
        const updates = recalculateColumnPositions(
          state.project.items,
          wallId,
          usableInfo.startOffset || 0
        );
        
        if (updates.length === 0) return;
        
        const items = state.project.items.map(item => {
          const update = updates.find(u => u.id === item.id);
          return update ? { ...item, ...update } : item;
        });
        
        state.updateProject({ items });
      },
      
      deleteItem: (itemId) => {
        const state = get();
        if (!state.project) {
            return;
        }
        
        // Cascade: if deleting a multi-column container, also remove its children.
        const target = state.project.items.find(i => i.id === itemId);
        const isContainerItem = target && (target.category === 'multi-column-container' || target.templateId === 'multi-column-container');
        const items = state.project.items.filter(item => {
          if (item.id === itemId) return false;
          if (isContainerItem && item.parentContainerId === itemId) return false;
          return true;
        });
        state.updateProject({ items });
        
        if (state.selectedItemId === itemId) {
          set({ selectedItemId: null });
        }
      },
      
      duplicateItem: (itemId) => {
        const state = get();
        if (!state.project) return;
        
        const original = state.project.items.find(item => item.id === itemId);
        if (!original) return;
        
        const newItem = {
          ...original,
          id: uuidv4(),
          x: original.x + 12, // Offset slightly
        };
        
        state.updateProject({
          items: [...state.project.items, newItem],
        });
        
        set({ selectedItemId: newItem.id });
      },
      
      /**
       * Get all item IDs in the same column as the selected item
       * Used for "Select Column" feature
       */
      selectColumn: (itemId) => {
        const state = get();
        if (!state.project || !state.activeWallId) return [];
        
        const wallId = state.project.items.find(i => i.id === itemId)?.wallId || state.activeWallId;
        const columnItemIds = getColumnItemIds(state.project.items, wallId, itemId);
        
        // Set the first item as selected and return all IDs for multi-select UI
        if (columnItemIds.length > 0) {
          set({ selectedItemId: columnItemIds[0] });
        }
        
        return columnItemIds;
      },
      
      /**
       * Duplicate an entire column (all items in the column)
       * Places the duplicated column immediately to the right of the original
       * with horizontal reflow for columns to the right
       */
      duplicateColumn: (itemId) => {
        const state = get();
        if (!state.project || !state.activeWallId) {
          return { success: false, error: 'No project or wall' };
        }
        
        const currentItem = state.project.items.find(item => item.id === itemId);
        if (!currentItem) {
          return { success: false, error: 'Item not found' };
        }
        
        const wallId = currentItem.wallId || state.activeWallId;
        const wall = state.project.walls.find(w => w.id === wallId);
        if (!wall) {
          return { success: false, error: 'Wall not found' };
        }
        
        // Get usable width info
        const usableInfo = calculateUsableWidth(
          state.project.buffersByWall,
          wallId,
          wall.length
        );
        
        // Check if duplication is possible
        const { canDuplicate, error, newX } = canDuplicateColumn(
          state.project.items,
          wallId,
          itemId,
          wall.length,
          usableInfo
        );
        
        if (!canDuplicate) {
          return { success: false, error };
        }
        
        // Create duplicates and get updates for existing items
        const { newItems, updates } = createColumnDuplicates(
          state.project.items,
          wallId,
          itemId,
          uuidv4
        );
        
        if (newItems.length === 0) {
          return { success: false, error: 'No items to duplicate' };
        }
        
        // Apply updates to existing items and add new items
        let updatedItems = state.project.items.map(item => {
          const update = updates.find(u => u.id === item.id);
          return update ? { ...item, ...update } : item;
        });
        
        // Add new items
        updatedItems = [...updatedItems, ...newItems];
        
        state.updateProject({ items: updatedItems });
        
        // Select the first item of the new column
        if (newItems.length > 0) {
          set({ selectedItemId: newItems[0].id });
        }
        
        return { success: true, newColumnItemIds: newItems.map(i => i.id) };
      },
      
      /**
       * Check if wall layout can be copied to another wall
       */
      canCopyWallLayoutTo: (sourceWallId, targetWallId) => {
        const state = get();
        if (!state.project) return { canCopy: false, error: 'No project' };
        
        return canCopyWallLayout(
          state.project.items,
          sourceWallId,
          targetWallId,
          state.project.walls,
          state.project.buffersByWall
        );
      },
      
      /**
       * Copy entire wall layout to another wall
       * Removes existing items on target wall and replaces with source layout
       */
      copyWallLayout: (sourceWallId, targetWallId, keepExisting = false) => {
        const state = get();
        if (!state.project) {
          return { success: false, error: 'No project' };
        }
        
        // Validate the copy
        const { canCopy, error } = canCopyWallLayout(
          state.project.items,
          sourceWallId,
          targetWallId,
          state.project.walls,
          state.project.buffersByWall
        );
        
        if (!canCopy) {
          return { success: false, error };
        }
        
        // Create new items for target wall
        const newItems = createWallLayoutCopy(
          state.project.items,
          sourceWallId,
          targetWallId,
          state.project.buffersByWall,
          uuidv4
        );
        
        if (newItems.length === 0) {
          return { success: false, error: 'No items to copy' };
        }
        
        // Get items not on target wall (or all items if keepExisting)
        let updatedItems;
        if (keepExisting) {
          updatedItems = [...state.project.items, ...newItems];
        } else {
          // Remove existing items on target wall, add new ones
          updatedItems = state.project.items.filter(i => i.wallId !== targetWallId);
          updatedItems = [...updatedItems, ...newItems];
        }
        
        state.updateProject({ items: updatedItems });
        
        // Switch to target wall
        set({ activeWallId: targetWallId });
        
        return { success: true, copiedCount: newItems.length };
      },
      
      moveItemToWall: (itemId, targetWallId) => {
        const state = get();
        if (!state.project) return;
        
        const items = state.project.items.map(item =>
          item.id === itemId ? { ...item, wallId: targetWallId } : item
        );
        
        state.updateProject({ items });
      },
      
      // Swap column with adjacent column (left or right)
      swapColumnWithAdjacent: (itemId, direction) => {
        const state = get();
        if (!state.project || !state.activeWallId) return;
        
        const wallItems = state.project.items.filter(item => item.wallId === state.activeWallId);
        
        // Group items into columns by X position
        const columnMap = new Map();
        wallItems.forEach(item => {
          const columnX = Math.round(item.x * 100) / 100;
          const columnKey = `${columnX}-${item.width}`;
          if (!columnMap.has(columnKey)) {
            columnMap.set(columnKey, { x: item.x, width: item.width, items: [] });
          }
          columnMap.get(columnKey).items.push(item);
        });
        
        // Convert to sorted array
        const columns = Array.from(columnMap.values()).sort((a, b) => a.x - b.x);
        
        // Find the column containing the selected item
        let currentColumnIndex = -1;
        for (let i = 0; i < columns.length; i++) {
          if (columns[i].items.some(item => item.id === itemId)) {
            currentColumnIndex = i;
            break;
          }
        }
        
        if (currentColumnIndex === -1) return;
        
        // Determine target column index
        const targetColumnIndex = direction === 'left' ? currentColumnIndex - 1 : currentColumnIndex + 1;
        
        // Check bounds
        if (targetColumnIndex < 0 || targetColumnIndex >= columns.length) {
          return; // Can't swap - at edge
        }
        
        const currentColumn = columns[currentColumnIndex];
        const targetColumn = columns[targetColumnIndex];
        
        // Calculate new X positions after swap
        // The columns swap positions, so we need to calculate new X values
        let newItems;
        
        if (direction === 'left') {
          // Current column moves left, target column moves right
          const newCurrentX = targetColumn.x;
          const newTargetX = targetColumn.x + currentColumn.width;
          
          newItems = state.project.items.map(item => {
            if (currentColumn.items.some(ci => ci.id === item.id)) {
              return { ...item, x: newCurrentX };
            }
            if (targetColumn.items.some(ti => ti.id === item.id)) {
              return { ...item, x: newTargetX };
            }
            return item;
          });
        } else {
          // Current column moves right, target column moves left
          const newTargetX = currentColumn.x;
          const newCurrentX = currentColumn.x + targetColumn.width;
          
          newItems = state.project.items.map(item => {
            if (targetColumn.items.some(ti => ti.id === item.id)) {
              return { ...item, x: newTargetX };
            }
            if (currentColumn.items.some(ci => ci.id === item.id)) {
              return { ...item, x: newCurrentX };
            }
            return item;
          });
        }
        
        state.updateProject({ items: newItems });
      },
      
      selectItem: (itemId) => set({ selectedItemId: itemId, isColumnSelected: false }),
      setIsColumnSelected: (val) => set({ isColumnSelected: val }),
      
      // Item count update
      updateItemCount: (itemId, newCount) => {
        const state = get();
        if (!state.project) return;
        
        const clampedCount = Math.max(1, Math.min(20, newCount));
        
        const items = state.project.items.map(item =>
          item.id === itemId ? { ...item, count: clampedCount } : item
        );
        
        state.updateProject({ items });
      },
      
      // Undo/Redo
      undo: () => {
        const state = get();
        if (state.historyIndex <= 0) return;
        
        const newIndex = state.historyIndex - 1;
        const projectData = JSON.parse(state.history[newIndex]);
        
        set({
          project: projectData,
          historyIndex: newIndex,
          selectedItemId: null,
        });
      },
      
      redo: () => {
        const state = get();
        if (state.historyIndex >= state.history.length - 1) return;
        
        const newIndex = state.historyIndex + 1;
        const projectData = JSON.parse(state.history[newIndex]);
        
        set({
          project: projectData,
          historyIndex: newIndex,
          selectedItemId: null,
        });
      },
      
      canUndo: () => get().historyIndex > 0,
      canRedo: () => get().historyIndex < get().history.length - 1,
      
      // (Zoom + Snap actions moved to createUiSlice — setZoom, zoomIn,
      //  zoomOut, toggleSnap, setSnapEnabled)
      
      // Style Settings (Global Accessories)
      updateStyleSettings: (updates) => {
        const state = get();
        if (!state.project) return;
        
        state.updateProject({
          styleSettings: { ...state.project.styleSettings, ...updates },
        });
      },
      
      // (setShowStyleSettingsModal moved to createUiSlice)
      
      // ==================== ADVANCED CORNER BUFFER ACTIONS ====================
      
      /**
       * Select a buffer (for showing controls)
       */
      selectBuffer: (bufferId) => {
        const state = get();
        if (!state.project) return;
        state.updateProject({ selectedBufferId: bufferId });
      },
      
      /**
       * Deselect buffer
       */
      deselectBuffer: () => {
        const state = get();
        if (!state.project) return;
        state.updateProject({ selectedBufferId: null });
      },
      
      /**
       * Update all buffer sizes when depth changes
       * bufferIn = 1.5 * depthIn
       */
      updateBufferSizes: () => {
        const state = get();
        if (!state.project) return;
        
        const { shapeType, room, buffersByWall } = state.project;
        if (shapeType === 'ONE_WALL' || !buffersByWall) return;
        
        const depthIn = room.defaultDepth || 24;
        const updatedBuffers = updateBufferSizes(buffersByWall, depthIn);
        
        state.updateProject({ buffersByWall: updatedBuffers });
      },
      
      /**
       * Change buffer pinned side (start/end)
       * @param {string} bufferId - Buffer ID
       * @param {string} newSide - 'start' or 'end'
       */
      changeBufferPinnedSide: (bufferId, newSide) => {
        const state = get();
        if (!state.project) return { success: false, error: 'No project' };
        
        const { buffersByWall, items, walls } = state.project;
        const result = changePinnedSide(buffersByWall, bufferId, newSide, items, walls);
        
        if (result.success) {
          state.updateProject({ buffersByWall: result.buffersByWall });
        }
        
        return result;
      },
      
      /**
       * Send buffer to another wall
       * @param {string} bufferId - Buffer ID
       * @param {string} targetWallId - Target wall ID
       */
      sendBufferToWall: (bufferId, targetWallId) => {
        const state = get();
        if (!state.project) return { success: false, error: 'No project' };
        
        const { buffersByWall, items, walls, shapeType, lShapeDoorSide } = state.project;
        const result = sendBufferToWall(buffersByWall, bufferId, targetWallId, items, walls, shapeType, lShapeDoorSide);
        
        if (result.success) {
          state.updateProject({ 
            buffersByWall: result.buffersByWall,
            selectedBufferId: null, // Deselect after move
          });
        }
        
        return result;
      },
      
      /**
       * Delete a buffer from the system
       * @param {string} bufferId - Buffer ID to delete
       */
      deleteBuffer: (bufferId) => {
        const state = get();
        if (!state.project) return { success: false, error: 'No project' };
        
        const { buffersByWall } = state.project;
        const result = deleteBuffer(buffersByWall, bufferId);
        
        if (result.success) {
          state.updateProject({ 
            buffersByWall: result.buffersByWall,
            selectedBufferId: null, // Deselect after delete
          });
        }
        
        return result;
      },
      
      /**
       * Resize a buffer
       * @param {string} bufferId - Buffer ID to resize
       * @param {number} newSizeIn - New size in inches
       */
      resizeBuffer: (bufferId, newSizeIn) => {
        const state = get();
        if (!state.project) return { success: false, error: 'No project' };
        
        const { buffersByWall, items, walls } = state.project;
        const result = resizeBuffer(buffersByWall, bufferId, newSizeIn, items, walls);
        
        if (result.success) {
          state.updateProject({ 
            buffersByWall: result.buffersByWall,
          });
        }
        
        return result;
      },
      
      /**
       * Get all valid walls for buffer placement
       * For L-Shape, depends on door side configuration
       */
      getValidWallsForBuffer: () => {
        const state = get();
        if (!state.project) return [];
        return getAllValidWalls(state.project.shapeType, state.project.lShapeDoorSide);
      },
      
      /**
       * Get buffers on active wall
       */
      getActiveWallBuffers: () => {
        const state = get();
        if (!state.project) return [];
        
        const { buffersByWall } = state.project;
        const { activeWallId } = state;
        
        return getBuffersOnWall(buffersByWall, activeWallId);
      },
      
      /**
       * Get usable width info for active wall
       */
      getActiveWallUsableInfo: () => {
        const state = get();
        if (!state.project) return null;
        
        const { buffersByWall, walls } = state.project;
        const { activeWallId } = state;
        const wall = walls.find(w => w.id === activeWallId);
        
        if (!wall) return null;
        
        return calculateUsableWidth(buffersByWall, activeWallId, wall.length);
      },
      
      // ==================== AUTH ACTIONS — moved to slices/authSlice.js ====================
      // (Phase D extraction May 6, 2026: signIn / signUp / signOut /
      //  setAuthModal / clearAuthError / setCurrentUser are now provided by
      //  `createAuthSlice` and spread into this store at construction time.
      //  Behavior, side-effects, and persist shape are byte-identical.)

      // ==================== PROJECT SAVE/LOAD (REAL API) ====================
      
      saveProjectToCloud: async () => {
        const state = get();
        if (!state.project || !state.currentUser) {
          return { success: false, error: 'Not authenticated' };
        }
        
        try {
          const projectPayload = {
            name: state.project.name,
            units: state.project.units || 'inches',
            closetType: state.project.shapeType,
            room: state.project.room,
            walls: state.project.walls,
            items: state.project.items,
            styleSettings: state.project.styleSettings || {},
            buffersByWall: state.project.buffersByWall || null, // IMPORTANT: Persist buffers
          };
          
          let projectId = state.project.cloudId;
          
          // Check if this project already exists in cloud
          if (projectId) {
            // Update existing
            await apiCall(`/api/projects/${projectId}`, {
              method: 'PUT',
              body: JSON.stringify(projectPayload),
            });
          } else {
            // Create new
            const saved = await apiCall('/api/projects', {
              method: 'POST',
              body: JSON.stringify(projectPayload),
            });
            
            projectId = saved.id;
            
            // Update local project with cloud ID
            set({
              project: { ...state.project, cloudId: projectId },
            });
          }
          
          // P1: Check if admin is editing a booking's design
          const adminBookingId = localStorage.getItem('admin-editing-booking-id');
          if (adminBookingId && state.currentUser?.role === 'admin') {
            try {
              // Update the booking with the new design
              await apiCall(`/api/admin/bookings/${adminBookingId}/design`, {
                method: 'PUT',
                body: JSON.stringify({
                  designId: projectId,
                  designSnapshot: JSON.stringify(state.project),
                  designName: state.project.name,
                  closetType: state.project.shapeType,
                }),
              });
              
              // Clear the admin editing state
              localStorage.removeItem('admin-editing-booking-id');
              localStorage.removeItem('admin-creating-for-customer');
              
              // Return success with booking update notification
              return { success: true, bookingUpdated: true };
            } catch (bookingError) {
              console.error('Failed to update booking with design:', bookingError);
              // Project saved but booking update failed
              return { success: true, bookingUpdateError: bookingError.message };
            }
          }
          
          return { success: true };
        } catch (error) {
          return { success: false, error: error.message };
        }
      },
      
      loadProjectsFromCloud: async () => {
        const state = get();
        if (!state.currentUser) {
          return { success: false, error: 'Not authenticated' };
        }
        
        try {
          const projects = await apiCall('/api/projects');
          set({ savedProjects: projects });
          return { success: true, projects };
        } catch (error) {
          return { success: false, error: error.message };
        }
      },
      
      loadProjectFromCloud: async (projectId) => {
        try {
          const project = await apiCall(`/api/projects/${projectId}`);
          
          // Ensure walls have all required properties (label, notes, orderIndex, height)
          const normalizedWalls = (project.walls || []).map((wall, index) => ({
            ...wall,
            label: wall.label || wall.id.replace('wall-', '').toUpperCase(),
            notes: wall.notes || '',
            orderIndex: wall.orderIndex ?? index,
            height: wall.height ?? null, // null means use global room height
          }));
          
          // Convert cloud format to local format
          const localProject = {
            id: project.id,
            cloudId: project.id,
            name: project.name,
            units: project.units,
            shapeType: project.closetType,
            room: project.room,
            walls: normalizedWalls,
            items: project.items,
            styleSettings: project.styleSettings || {},
            buffersByWall: project.buffersByWall, // IMPORTANT: Preserve saved buffers
            createdAt: project.createdAt,
            updatedAt: project.updatedAt,
            references: { uploadedImages: [] },
            lastCartLink: null,
          };
          
          get().loadProject(localProject);
          return { success: true };
        } catch (error) {
          return { success: false, error: error.message };
        }
      },
      
      deleteProjectFromCloud: async (projectId) => {
        try {
          await apiCall(`/api/projects/${projectId}`, {
            method: 'DELETE',
          });
          
          // Refresh list
          await get().loadProjectsFromCloud();
          return { success: true };
        } catch (error) {
          return { success: false, error: error.message };
        }
      },
      
      // (setShowLoadProjectModal moved to createUiSlice)
      
      // (Modal actions moved to createUiSlice — setShowCartLinkModal,
      //  setShowImageUploadModal, setShowEditRoomModal,
      //  setShowResponsiveWarning)
      
      // Image upload
      addUploadedImage: (imageData) => {
        const state = get();
        if (!state.project) return;
        
        state.updateProject({
          references: {
            ...state.project.references,
            uploadedImages: [...state.project.references.uploadedImages, imageData],
          },
        });
      },
      
      // Cart link
      generateCartLink: () => {
        const state = get();
        if (!state.project) return null;
        
        const encoded = btoa(JSON.stringify(state.project));
        const link = `${window.location.origin}?design=${encoded}`;
        
        state.updateProject({ lastCartLink: link });
        return link;
      },
      
      // Export/Import
      exportJSON: () => {
        const state = get();
        if (!state.project) return null;
        return JSON.stringify(state.project, null, 2);
      },
      
      importJSON: (jsonString) => {
        try {
          const data = JSON.parse(jsonString);
          if (!data.id || !data.walls) {
            throw new Error('Invalid project format');
          }
          get().loadProject(data);
          return { success: true };
        } catch (error) {
          return { success: false, error: error.message };
        }
      },
      
      // Room editing
      updateRoom: (updates) => {
        const state = get();
        if (!state.project) return;
        
        state.updateProject({
          room: { ...state.project.room, ...updates },
        });
        
        // If depth changed, update buffer sizes
        if (updates.defaultDepth !== undefined) {
          state.updateBufferSizes();
        }
      },
      
      // Shape conversion
      changeShapeType: (newShapeType) => {
        const state = get();
        if (!state.project) return;
        
        const currentWalls = state.project.walls;
        const newWalls = createWallsForShape(newShapeType);
        
        // Preserve existing wall data where possible
        newWalls.forEach((newWall, index) => {
          if (currentWalls[index]) {
            newWall.length = currentWalls[index].length;
            newWall.notes = currentWalls[index].notes;
            newWall.name = currentWalls[index].name;
          }
        });
        
        // Handle items on removed walls
        let items = state.project.items;
        if (newWalls.length < currentWalls.length) {
          const validWallIds = new Set(newWalls.map(w => w.id));
          items = items.filter(item => validWallIds.has(item.wallId));
        }
        
        // Initialize buffers for new shape type
        const depthIn = state.project.room?.defaultDepth || 24;
        const buffersByWall = initializeBuffers(newShapeType, depthIn);
        
        state.updateProject({
          shapeType: newShapeType,
          walls: newWalls,
          items,
          buffersByWall,
          selectedBufferId: null,
        });
        
        // Ensure active wall is valid
        if (!newWalls.find(w => w.id === state.activeWallId)) {
          set({ activeWallId: newWalls[0].id });
        }
      },
      
      // ==================== EDIT MODE ====================
      
      // Enter edit mode - saves snapshot and navigates to wizard
      enterEditMode: () => {
        const state = get();
        if (!state.project) return;
        
        // Save snapshot of current project state
        set({
          editMode: true,
          editModeSnapshot: JSON.stringify(state.project),
          currentPage: 'closet-type',
        });
        
        // Store current shape type and door configs for the wizard
        localStorage.setItem('selectedShapeType', state.project.shapeType);
        if (state.project.lShapeDoorSide) {
          localStorage.setItem('lShapeDoorSide', state.project.lShapeDoorSide);
        }
        if (state.project.uShapeDoorConfig) {
          localStorage.setItem('uShapeDoorConfig', state.project.uShapeDoorConfig);
        }
      },
      
      // Save changes from edit mode and return to editor
      saveEditModeChanges: (shapeType, roomConfig, wallLengths, wallHeights = {}, lShapeDoorSide = null, uShapeDoorConfig = null) => {
        const state = get();
        if (!state.project) return;
        
        // Determine the effective door side
        const effectiveDoorSide = lShapeDoorSide ?? state.project.lShapeDoorSide ?? L_SHAPE_DOOR_SIDES.RIGHT;
        
        // Determine the effective U-shape door config
        const effectiveUDoorConfig = uShapeDoorConfig ?? state.project.uShapeDoorConfig ?? U_SHAPE_DOOR_CONFIGS.SINGLE;
        
        // Check if L-shape door side changed (requires FULL RESET)
        const doorSideChanged = shapeType === SHAPE_TYPES.L_SHAPE && 
          state.project.shapeType === SHAPE_TYPES.L_SHAPE &&
          effectiveDoorSide !== state.project.lShapeDoorSide;
        
        // Check if U-shape door config changed (requires Wall B rebuild)
        const uDoorConfigChanged = shapeType === SHAPE_TYPES.U_SHAPE &&
          state.project.shapeType === SHAPE_TYPES.U_SHAPE &&
          effectiveUDoorConfig !== state.project.uShapeDoorConfig;
        
        // Pass door side to create correct wall configuration
        const newWalls = createWallsForShape(shapeType, null, effectiveDoorSide);
        
        // For U-shape Double Door, mark Wall B as not designed
        if (shapeType === SHAPE_TYPES.U_SHAPE && effectiveUDoorConfig === U_SHAPE_DOOR_CONFIGS.DOUBLE) {
          const wallB = newWalls.find(w => w.id === 'wall-b');
          if (wallB) wallB.designed = false;
        }
        
        // Apply wall lengths and heights
        newWalls.forEach((newWall) => {
          if (doorSideChanged) {
            newWall.length = wallLengths[newWall.id] || 96;
            newWall.height = wallHeights[newWall.id] ?? null;
          } else {
            newWall.length = wallLengths[newWall.id] || newWall.length;
            newWall.height = wallHeights[newWall.id] ?? null;
          }
        });
        
        // Handle items cleanup
        let items = state.project.items;
        if (doorSideChanged) {
          items = [];
        } else if (uDoorConfigChanged) {
          // U-shape door config changed: remove items on Wall B (it toggles between designed/not-designed)
          items = items.filter(item => item.wallId !== 'wall-b');
        } else if (shapeType !== state.project.shapeType) {
          const validWallIds = new Set(newWalls.map(w => w.id));
          items = items.filter(item => validWallIds.has(item.wallId));
        }
        
        // Handle buffers
        const depthIn = roomConfig?.defaultDepth || state.project.room?.defaultDepth || 24;
        let buffersByWall = state.project.buffersByWall;
        
        if (doorSideChanged || shapeType !== state.project.shapeType || uDoorConfigChanged) {
          if (shapeType === SHAPE_TYPES.U_SHAPE && effectiveUDoorConfig === U_SHAPE_DOOR_CONFIGS.DOUBLE) {
            // Double Door: no corner buffers
            buffersByWall = {};
          } else {
            buffersByWall = initializeBuffers(shapeType, depthIn, effectiveDoorSide);
          }
        }
        
        // Update project with new settings
        state.updateProject({
          shapeType,
          lShapeDoorSide: effectiveDoorSide,
          uShapeDoorConfig: effectiveUDoorConfig,
          room: { ...state.project.room, ...roomConfig },
          walls: newWalls,
          items,
          buffersByWall,
          selectedBufferId: null,
        });
        
        // Set active wall to first designed wall
        const firstDesigned = newWalls.find(w => w.designed !== false);
        const activeWallId = firstDesigned ? firstDesigned.id : newWalls[0].id;
        
        set({
          editMode: false,
          editModeSnapshot: null,
          activeWallId,
          currentPage: 'editor',
        });
      },
      
      // Cancel edit mode and restore snapshot
      cancelEditMode: () => {
        const state = get();
        
        if (state.editModeSnapshot) {
          const restoredProject = JSON.parse(state.editModeSnapshot);
          set({
            project: restoredProject,
            editMode: false,
            editModeSnapshot: null,
            currentPage: 'editor',
          });
        } else {
          set({
            editMode: false,
            editModeSnapshot: null,
            currentPage: 'editor',
          });
        }
      },
      
      // Check how many items would be lost when changing shape
      getItemsOnRemovedWalls: (newShapeType) => {
        const state = get();
        if (!state.project) return [];
        
        const newWalls = createWallsForShape(newShapeType);
        const validWallIds = new Set(newWalls.map(w => w.id));
        
        return state.project.items.filter(item => !validWallIds.has(item.wallId));
      },
    }),
    {
      name: 'eoc-closet-storage',
      partialize: (state) => ({
        // Core project data
        project: state.project,
        // Navigation state - persist current page for hard refresh support
        currentPage: state.currentPage,
        // Editor state
        activeWallId: state.activeWallId,
        selectedItemId: state.selectedItemId,
        // UI preferences
        zoom: state.zoom,
        snapEnabled: state.snapEnabled,
        // Don't persist auth state in zustand - use localStorage directly for token
      }),
    }
  )
);

export default useStore;
export { SHAPE_TYPES, L_SHAPE_DOOR_SIDES, U_SHAPE_DOOR_CONFIGS };
